/* eslint-disable react/button-has-type */
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Drawer, IconButton } from '@mui/material';
import Iconify from '../../../components/iconify';
import AccountPopover from '../header/AccountPopover';

// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
//
import Master from './config2';
import logo from '../../../media/Techveel-newlogo.jpg';

// ----------------------------------------------------------------------

const NAV_WIDTH = 250;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  isNavOpen: PropTypes.bool,
  onToggleNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav, isNavOpen = true, onToggleNav }) {
  const { pathname } = useLocation();

  const navigate = useNavigate();

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        background: 'linear-gradient(180deg, #f8fbff 0%, #f3f6fb 100%)',
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: 'rgba(255,255,255,0.45)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
              color: '#fff',
              boxShadow: '0 8px 18px rgba(37, 99, 235, 0.24)',
            }}
          >
            <Iconify icon="eva:menu-2-fill" width={16} />
          </Box>
        </Box>

        <IconButton
          onClick={onToggleNav}
          sx={{
            color: 'primary.main',
            backgroundColor: alpha('#1d4ed8', 0.08),
            '&:hover': { backgroundColor: alpha('#1d4ed8', 0.12) },
          }}
        >
          <Iconify icon={isNavOpen ? 'eva:arrow-ios-back-fill' : 'eva:arrow-ios-forward-fill'} />
        </IconButton>
      </Box>

      <Box sx={{ mb: 2, m: 1.5 }}>
        <Link underline="none">
          <StyledAccount
            sx={{
              background: 'linear-gradient(135deg, rgba(29,78,216,0.12), rgba(59,130,246,0.04))',
              border: '1px solid rgba(59,130,246,0.12)',
              minHeight: 72,
            }}
          >
            <button
              onClick={() => {
                navigate('/dashboard');
              }}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <img
                src={logo}
                style={{ objectFit: 'contain', maxWidth: '58%', mixBlendMode: 'multiply' }}
                alt="techveel logo"
              />
            </button>
          </StyledAccount>
        </Link>
      </Box>

      <Box sx={{ px: 1.5, py: 0.5, '& .MuiListItemButton-root': { minHeight: 42 } }}>
        <NavSection data={Master} />
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.7), rgba(241,245,249,0.9))',
        }}
      >
        <AccountPopover />
      </Box>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: isDesktop && isNavOpen ? NAV_WIDTH : 0 },
      }}
    >
      {isDesktop && !isNavOpen && (
        <IconButton
          onClick={onToggleNav}
          sx={{
            position: 'fixed',
            left: 12,
            top: 18,
            zIndex: 1300,
            background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
            color: '#fff',
            boxShadow: '0 10px 24px rgba(29, 78, 216, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
            },
          }}
        >
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {isDesktop ? (
        <Drawer
          open={isNavOpen}
          variant="persistent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'solid',
              borderRightWidth: 1,
              borderColor: 'rgba(148, 163, 184, 0.28)',
              boxShadow: '14px 0 28px rgba(15, 23, 42, 0.08)',
              backgroundImage: 'none',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)',
              backgroundImage: 'none',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
